// Credits, as a dialog over the page: the balance, a top-off dialog, and the
// activity. The activity is the consumer account's own ledger from GET
// /v1/account; only if that can't be read does it fall back to this session's
// charges and refunds with sample history that says so. Topping off isn't
// connected. Both dialogs are the kit's Dialog, which supplies focus and Escape.
// See Design > Names and definitions, "The Console", and Design > Exchange, "Money".

import { useState } from "preact/hooks";
import { Button, DataTable, Dialog, EmptyState, Field, Icon, List, ListRow, Metric, MoneyInput, PillGroup, StatusBadge } from "../../../ui/src/index.ts";
import type { DialogRailItem, FeedbackTone, TableColumn } from "../../../ui/src/index.ts";
import { ledgerLine } from "../domain/ledger.ts";
import { usd } from "../domain/money.ts";
import { closeCredits } from "./actions.ts";
import { useStore } from "./store.ts";
import { NotConnected } from "./ui.tsx";

const PRESETS = [5, 10, 25];

const RAIL: ReadonlyArray<DialogRailItem> = [
  { label: "Profile", icon: "user", unavailable: "Not connected in this prototype" },
  { label: "Credits", icon: "credits", current: true },
];

function TopOff({ balance, onClose }: { balance: number | null; onClose: () => void }) {
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const value = custom === "" ? amount : Number(custom);
  const valid = Number.isFinite(value) && value > 0;
  return (
    <Dialog open onClose={onClose} title="Top off credits" description="Add US dollars to your balance. Answers are charged from it."
      actions={<>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
        <NotConnected variant="solid" label="Add credits">Add {valid ? usd(value) : ""} in credits</NotConnected>
      </>}>
      <div class="cs-topoff">
        <div class="cs-topoff__group">
          <span class="cs-topoff__label" id="topoff-amount">Amount</span>
          <PillGroup label="Amount" options={PRESETS.map((p) => ({ value: String(p), label: usd(p) }))}
            value={custom === "" ? String(amount) : ""} onChange={(v) => { setAmount(Number(v)); setCustom(""); }} />
        </div>
        <Field id="topoff-custom" label="Or enter an amount">
          {(attrs) => <MoneyInput {...attrs} value={custom} placeholder={String(amount)} onInput={(e) => setCustom(e.currentTarget.value.trim())} />}
        </Field>
        <div class="cs-topoff__row">
          <div class="cs-topoff__stack"><span class="cs-topoff__muted">Pay with</span><span class="mono">[PAYMENT METHOD]</span></div>
          <NotConnected label="Change payment method" size="sm">Change</NotConnected>
        </div>
        <div class="cs-topoff__row">
          <span class="cs-topoff__muted">New balance</span>
          <strong class="mono cs-topoff__total">{balance === null || !valid ? "–" : usd(balance + value)}</strong>
        </div>
        <p class="cs-topoff__note">In this prototype, topping off adds simulated credits and charges no payment method.</p>
      </div>
    </Dialog>
  );
}

/** One line of the activity table, whichever of the three sources it came from. */
type ActivityRow = { key: string; type: string; what: string; hub: string; amount: number; when: string };

const LEDGER_TONES = new Map<string, FeedbackTone>([["Refund", "success"], ["Grant", "success"]]);
const toneFor = (type: string): FeedbackTone => LEDGER_TONES.get(type) ?? "neutral";
const timeOf = (at: number) => new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const COLUMNS: ReadonlyArray<TableColumn<ActivityRow>> = [
  { id: "type", label: "Type", render: (r) => <StatusBadge tone={toneFor(r.type)}>{r.type}</StatusBadge> },
  { id: "what", label: "What", render: (r) => r.what },
  { id: "hub", label: "Hub", render: (r) => <span class="mono">{r.hub}</span> },
  { id: "amount", label: "Amount", numeric: true, render: (r) => <span class="mono">{r.amount > 0 ? "+" : ""}{usd(r.amount)}</span> },
  { id: "time", label: "Time", render: (r) => r.when },
];
const rowKey = (r: ActivityRow) => r.key;

export function CreditsDialog() {
  const balance = useStore((s) => s.balance);
  const ledger = useStore((s) => s.ledger);
  const account = useStore((s) => s.account);
  const history = useStore((s) => s.content.sample.history);
  const [topOff, setTopOff] = useState(false);

  const accountRows: ActivityRow[] = (account?.ledger ?? []).map((row) => {
    const e = ledgerLine(row);
    return {
      key: `${row.kind}-${row.createdAt}-${row.answerId ?? ""}`, type: e.type, what: e.what, hub: "–", amount: e.amount,
      when: new Date(e.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
    };
  });
  const sessionRows: ActivityRow[] = ledger.map((e) => ({ key: `${e.type}-${e.answerId}`, type: e.type, what: e.what, hub: e.hub, amount: e.amount, when: timeOf(e.at) }));
  const sampleRows: ActivityRow[] = history.map((h, i) => ({ key: `sample-${i}`, type: h.type, what: h.what, hub: h.hub ?? "–", amount: h.amount, when: `${h.daysAgo} days ago` }));

  let activity;
  if (account) {
    activity = account.ledger.length === 0
      ? <EmptyState class="cs-credits__empty">No charges or refunds yet.</EmptyState>
      : <DataTable caption="Activity" columns={COLUMNS} rows={accountRows} rowKey={rowKey} />;
  } else {
    activity = <>
      {ledger.length === 0 && <EmptyState class="cs-credits__empty">No charges or refunds in this session yet.</EmptyState>}
      <DataTable caption="Activity" columns={COLUMNS} rowKey={rowKey} groups={[
        ...(ledger.length > 0 ? [{ key: "session", title: "This session", rows: sessionRows }] : []),
        { key: "sample", title: "Sample data", summary: "The rows below are sample data, not your account's history.", rows: sampleRows },
      ]} />
    </>;
  }

  return (
    <>
      <Dialog open onClose={closeCredits} size="wide" rail={{ title: "Settings", items: RAIL }} title="Credits"
        description={<StatusBadge>Simulated credits in this prototype</StatusBadge>}>
        <div class="cs-credits">
          <div class="cs-credits__balance">
            <Metric label="Available credits" value={usd(balance)} context="US dollars. Shows – if your balance can't be read after an answer." />
            <Button variant="solid" onClick={() => setTopOff(true)}><Icon name="plus" size={16} />Top off credits</Button>
          </div>

          <List class="cs-credits__rows">
            <ListRow name="Automatic top-off" sub="Adds [AMOUNT] when the balance falls below [LOW BALANCE]"
              aside={<NotConnected label="Automatic top-off" size="sm">Set up</NotConnected>} />
            <ListRow name="Payment method" sub={<span class="mono">[PAYMENT METHOD]</span>}
              aside={<NotConnected label="Edit payment method" size="sm">Edit</NotConnected>} />
            <ListRow name="Billing address" sub="[BILLING ADDRESS]"
              aside={<NotConnected label="Edit billing address" size="sm">Edit</NotConnected>} />
          </List>

          <section class="cs-credits__activity" aria-labelledby="credits-activity">
            <div class="cs-credits__head"><h3 id="credits-activity">Activity</h3><span>Only answers are charged. Other responses are free.</span></div>
            {activity}
          </section>
        </div>
      </Dialog>
      {topOff && <TopOff balance={balance} onClose={() => setTopOff(false)} />}
    </>
  );
}
